import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiSave, FiArrowLeft, FiInbox, FiAlertTriangle } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { usePreferences } from '../../context/PreferencesContext';
import {
  createReceipt,
  getDonationCategories,
  getDerasarPersonUsers,
  getOfferings,
  getAllLedgerAccounts,
  getLedgerBalanceMap,
} from '../../services/api';
import { LedgerAccountSelect, SearchableSelect } from '../../components/common';
import { formatDate, today } from '../../utils/dateUtils';
import { toDerasarPersonOptions } from '../../utils/derasarPersonOptions';
import {
  getPreferredPostingFinancialYear,
  getUnlockedFinancialYears,
} from '../../utils/financialYears';
import OfferingsMemberSelect from '../Offerings/OfferingsMemberSelect';
import styles from './NewReceiptsPage.module.css';
import aStyles from '../Accounting/Accounting.module.css';

import { fmt, fmtCurrency } from '../../utils/formatNumber';

const PAYMENT_MODES = [
  { value: 'cash', label: 'Cash' },
  { value: 'upi', label: 'UPI' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'cheque', label: 'Cheque' },
];

const RECEIPT_TYPES = [
  { value: 'standard',  label: 'Standard Donation' },
  { value: 'gold',      label: 'Gold Donation' },
  { value: 'item',      label: 'Item Donation' },
  { value: 'anonymous', label: 'Shah A/C (Walk-in)' },
  { value: 'bhandar',   label: 'Bhandar (Cash Box)' },
];

// -- Helpers ----------------------------------------------------------------

/** Block decimal point / exponent / sign keys so only whole numbers can be typed */
const blockNonInteger = (e) => {
  if (['.', ',', 'e', 'E', '+', '-'].includes(e.key)) e.preventDefault();
};

/** Coerce any input to a whole-number string (truncates decimals, e.g. pasted "501.44" → "501") */
const toIntString = (v) => String(v ?? '').split('.')[0].replace(/[^\d]/g, '');

/** Prevent mouse-wheel scroll from changing a focused number input's value */
const blockWheel = (e) => e.target.blur();

/** Debit accounts for receipt - must be Asset (cash, bank, gold) */
const debitAccountsForType = (receiptType, accounts) => {
  if (receiptType === 'gold') return accounts.filter(a => a.accountType === 'gold');
  if (receiptType === 'item') return accounts.filter(a => ['fixed_asset', 'inventory', 'current_asset', 'suspense'].includes(a.accountType));
  return accounts.filter(a => ['cash', 'bank'].includes(a.accountType));
};

const defaultDebitForMode = (mode, accounts) => {
  if (mode === 'cash') {
    return accounts.find(a => a.accountType === 'cash' && a.isSystem) || null;
  }
  const banks = accounts.filter(a => a.accountType === 'bank');
  return banks.length === 1 ? banks[0] : null;
};

const modeForAccount = (acct, currentMode) => {
  if (!acct) return currentMode;
  if (acct.accountType === 'cash') return 'cash';
  if (acct.accountType === 'bank') return ['upi', 'bank_transfer', 'cheque'].includes(currentMode) ? currentMode : 'bank_transfer';
  return currentMode;
};

const getPledgeCategoryId = (pl) => String(pl?.DonationCategory?.id || pl?.donationCategory?.id || pl?.donationCategoryId?.id || pl?.donationCategoryId || '');
const getPledgeDate = (pl) => {
  if (!pl?.offeringDate) return '';
  return pl.offeringDate.includes('T') ? pl.offeringDate.slice(0, 10) : pl.offeringDate;
};

// --------------------------------------------------------------------------

const NewReceiptsPage = () => {
  const {
    activeDerasarId,
    visibleFinancialYears,
    activeFinancialYearId,
  } = useAuth();
  const { preferences } = usePreferences();
  const navigate = useNavigate();
  const location = useLocation();
  const prefill = location.state || {};

  // Store prefill values in refs so they persist after state is cleared
  const prefillDonorIdRef = useRef(prefill.prefillDonorId || '');
  const prefillOfferingIdRef = useRef(prefill.prefillOfferingId || '');
  const prefillFinancialYearIdRef = useRef(prefill.prefillFinancialYearId || '');
  const prefillOfferingDateRef = useRef(prefill.prefillOfferingDate || '');
  const allowAdvancePaymentRef = useRef(prefill.allowAdvancePayment || false);

  // Reference data
  const [categories, setCategories] = useState([]);
  const [financialYears, setFY] = useState([]);
  const [personUsers, setPersonUsers] = useState([]);
  const [allAccounts, setAllAccounts] = useState([]); // all ledger accounts (populated with ledgerGroupId)
  const [balanceMap, setBalanceMap] = useState({});
  const [pledges, setPledges] = useState([]);
  const [loadingRef, setLoadingRef] = useState(true);
  const [loadingPledges, setLoadingPledges] = useState(false);

  // 'bank' in preferences maps to 'bank_transfer' in receipt forms
  const RECEIPT_MODES = new Set(['cash', 'upi', 'bank_transfer', 'cheque']);
  const prefMode = preferences.defaultPaymentMode === 'bank' ? 'bank_transfer' : preferences.defaultPaymentMode;
  const initialPaymentMode = RECEIPT_MODES.has(prefMode) ? prefMode : 'cash';

  // Form state
  const [receiptDate, setReceiptDate] = useState(prefillOfferingDateRef.current || today());
  const [financialYearId, setFinancialYearId] = useState('');
  const [receiptType, setReceiptType] = useState('standard');
  const [paymentMode, setPaymentMode] = useState(initialPaymentMode);
  const [debitAcctId, setDebitAcctId] = useState('');   // Asset account (Dr)
  const [donorId, setDonorId] = useState('');
  const [selectedOfferingIds, setSelected] = useState([]);
  const [payAmounts, setPayAmounts] = useState({});
  const [showAdvancePayments, setShowAdvance] = useState(allowAdvancePaymentRef.current);
  const [pledgeFilterCategory, setPledgeFilterCategory] = useState('');
  const [pledgeFilterFromDate, setPledgeFilterFromDate] = useState('');
  const [pledgeFilterToDate, setPledgeFilterToDate] = useState('');
  const [autoSettleAmount, setAutoSettleAmount] = useState('');
  const [manualReceiptNo, setManualReceiptNo] = useState('');
  const [donationCategoryId, setCatId] = useState('');
  const [amountInr, setAmountInr] = useState('');
  const [goldGrams, setGoldGrams] = useState('');
  const [goldPurity, setGoldPurity] = useState('22');
  const [valuationRate, setValuationRate] = useState('');
  const [goldManualAmt, setGoldManualAmt] = useState('');
  const [referenceNo, setReferenceNo] = useState('');
  const [narration, setNarration] = useState('');
  const [saving, setSaving] = useState(false);
  const [sendEmail, setSendEmail] = useState(true);
  const [donorPhone, setDonorPhone] = useState('');
  const [donorName, setDonorName] = useState('');

  const donorPrefillApplied = useRef(false);
  const stateCleaned = useRef(false);

  // Apply donor prefill from navigation state (e.g. from Offerings page Receipt button)
  useEffect(() => {
    if (!prefillDonorIdRef.current || donorPrefillApplied.current) return;
    donorPrefillApplied.current = true;
    setDonorId(prefillDonorIdRef.current);
  }, []);

  // Clear navigation state after donor prefill is applied
  useEffect(() => {
    if (!donorPrefillApplied.current || stateCleaned.current) return;
    stateCleaned.current = true;
    navigate(location.pathname, { replace: true, state: null });
  }, [donorPrefillApplied, navigate, location.pathname]);

  // -- Derived ---------------------------------------------------------------
  const personUsers_mapped = toDerasarPersonOptions(personUsers);
  const selectedDonor = personUsers_mapped.find((o) => String(o.id) === String(donorId));

  const isGold = receiptType === 'gold';
  const isItem = receiptType === 'item';
  const isAnonymous = receiptType === 'anonymous';
  const isBhandar = receiptType === 'bhandar';
  const hasPledges = selectedOfferingIds.length > 0;

  const getPledgeCategoryName = (pl) => pl?.DonationCategory?.name || pl?.donationCategory?.name || pl?.donationCategoryId?.name || '';
  const visiblePledges = useMemo(() => {
    return pledges
      .filter((pl) => {
        if (showAdvancePayments) return true;
        const d = getPledgeDate(pl);
        return !d || d <= receiptDate;
      })
      .filter((pl) => {
        const d = getPledgeDate(pl);
        if (pledgeFilterCategory && getPledgeCategoryId(pl) !== pledgeFilterCategory) return false;
        if (pledgeFilterFromDate && d && d < pledgeFilterFromDate) return false;
        if (pledgeFilterToDate && d && d > pledgeFilterToDate) return false;
        return true;
      })
      .sort((a, b) => (
        getPledgeDate(a).localeCompare(getPledgeDate(b))
        || String(a.id).localeCompare(String(b.id), undefined, { numeric: true })
      ));
  }, [pledges, showAdvancePayments, receiptDate, pledgeFilterCategory, pledgeFilterFromDate, pledgeFilterToDate]);

  const pledgeCategoryOptions = useMemo(() => {
    const byId = new Map();
    pledges.forEach((pl) => {
      const id = getPledgeCategoryId(pl);
      if (id && !byId.has(id)) byId.set(id, getPledgeCategoryName(pl));
    });
    return [...byId.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [pledges]);

  const selectedVisibleCount = visiblePledges.filter((pl) => selectedOfferingIds.includes(pl.id)).length;
  const allVisibleSelected = visiblePledges.length > 0 && selectedVisibleCount === visiblePledges.length;

  const applyAutoSettleAmount = () => {
    const target = Math.round(parseFloat(autoSettleAmount || 0));
    if (!Number.isFinite(target) || target <= 0) {
      toast.error('Enter a partial amount greater than zero');
      return;
    }

    let remaining = target;
    const nextSelected = [];
    const nextAmounts = {};

    for (const pl of visiblePledges) {
      if (remaining <= 0) break;
      const pending = Math.floor(parseFloat(pl.pendingAmount || 0));
      if (pending <= 0) continue;
      const settled = Math.min(pending, remaining);
      nextSelected.push(pl.id);
      nextAmounts[pl.id] = String(settled);
      remaining -= settled;
    }

    setSelected(nextSelected);
    setPayAmounts(nextAmounts);

    if (remaining > 0) {
      toast.info(`Selected all visible offerings for ${fmtCurrency(target - remaining)}. ${fmtCurrency(remaining)} is still unallocated.`);
    }
  };

  const clearPledgeFilters = () => {
    setPledgeFilterCategory('');
    setPledgeFilterFromDate('');
    setPledgeFilterToDate('');
  };

  const debitAccounts = debitAccountsForType(receiptType, allAccounts);
  const computedGoldAmt = goldGrams && valuationRate
    ? parseFloat(goldGrams) * parseFloat(valuationRate)
    : null;

  const totalAmount = isGold
    ? (computedGoldAmt !== null ? computedGoldAmt : parseFloat(goldManualAmt || 0))
    : hasPledges
      ? selectedOfferingIds.reduce((s, id) => s + parseFloat(payAmounts[id] || 0), 0)
      : parseFloat(amountInr || 0);

  const selectedDebitAcct = allAccounts.find(a => String(a.id) === String(debitAcctId));
  const selectedCategory = categories.find(c => c.id === donationCategoryId);
  const unlockedFinancialYears = useMemo(() => getUnlockedFinancialYears(financialYears), [financialYears]);
  const selectedFinancialYear = useMemo(
    () => financialYears.find((financialYear) => String(financialYear.id) === String(financialYearId)) || null,
    [financialYears, financialYearId],
  );
  const fyStart = selectedFinancialYear?.startDate?.slice(0, 10) || '';
  const fyEnd = selectedFinancialYear?.endDate?.slice(0, 10) || '';

  // Preview credit lines (for display only - now always one credit account)
  const creditLineLabel = hasPledges
    ? `${selectedOfferingIds.length} offering${selectedOfferingIds.length > 1 ? 's' : ''} settled`
    : selectedCategory?.name || null;

  // -- Load reference data ---------------------------------------------------
  useEffect(() => {
    if (!activeDerasarId) return;
    Promise.all([
      getDonationCategories(activeDerasarId),
      Promise.resolve({ data: visibleFinancialYears }),
      getDerasarPersonUsers(activeDerasarId),
      getAllLedgerAccounts(activeDerasarId),
    ])
      .then(([catRes, fyRes, uRes, accRes]) => {
        setCategories(catRes.data);
        setFY(fyRes.data);
        const preferredFinancialYear = prefillFinancialYearIdRef.current
          ? fyRes.data.find((financialYear) => String(financialYear.id) === String(prefillFinancialYearIdRef.current))
          : (fyRes.data.find((financialYear) => String(financialYear.id) === String(activeFinancialYearId))
            || getPreferredPostingFinancialYear(fyRes.data));
        if (preferredFinancialYear) {
          setFinancialYearId(String(preferredFinancialYear.id));
        }
        setPersonUsers(toDerasarPersonOptions(uRes.data));
        const accounts = accRes.data.filter(a => a.isActive);
        setAllAccounts(accounts);

        // Default debit: cash in hand (system)
        const cashAccounts = accounts.filter(a => a.accountType === 'cash');
        const initial = cashAccounts.find(a => a.isSystem) || cashAccounts[0] || null;
        if (initial) setDebitAcctId(initial.id);

      })
      .catch(() => toast.error('Failed to load form data'))
      .finally(() => setLoadingRef(false));
  }, [activeDerasarId, visibleFinancialYears, activeFinancialYearId]);

  useEffect(() => {
    if (!activeDerasarId) return;
    setFY(visibleFinancialYears);
    if (activeFinancialYearId) setFinancialYearId(String(activeFinancialYearId));
  }, [activeDerasarId, visibleFinancialYears, activeFinancialYearId]);

  // -- Load ledger balances for the debit-account dropdown -------------------
  useEffect(() => {
    if (!activeDerasarId || !financialYearId) { setBalanceMap({}); return; }
    getLedgerBalanceMap(activeDerasarId, financialYearId)
      .then((res) => setBalanceMap(res.data.data || res.data || {}))
      .catch(() => setBalanceMap({}));
  }, [activeDerasarId, financialYearId]);


  // -- Load pledges when donor changes --------------------------------------
  useEffect(() => {
    setPledges([]);
    setSelected([]);
    setPayAmounts({});
    if (!donorId || !activeDerasarId) return;
    setLoadingPledges(true);
    Promise.all([
      getOfferings({ derasarId: activeDerasarId, userId: donorId, status: 'pending' }),
      getOfferings({ derasarId: activeDerasarId, userId: donorId, status: 'partial' }),
    ])
      .then(([a, b]) => setPledges([...a.data, ...b.data]))
      .catch(() => { })
      .finally(() => setLoadingPledges(false));
  }, [donorId, activeDerasarId]);

  // Auto-select the prefill offering once pledges have loaded (one-shot)
  useEffect(() => {
    if (!prefillOfferingIdRef.current || pledges.length === 0) return;
    const match = pledges.find((p) => String(p.id) === prefillOfferingIdRef.current);
    if (!match) return;
    setSelected([match.id]);
    setPayAmounts({ [match.id]: String(match.pendingAmount) });
    prefillOfferingIdRef.current = ''; // Clear after applying
  }, [pledges]);

  // -- Auto-derive category from selected offerings ---------------------------
  useEffect(() => {
    if (!hasPledges) {
      if (receiptType !== 'gold' && receiptType !== 'item' && receiptType !== 'bhandar') {
        setCatId('');
      }
      return;
    }
    const catIds = [...new Set(
      selectedOfferingIds.map((id) => {
        const pl = pledges.find((p) => p.id === id);
        return pl?.donationCategoryId?.id || pl?.donationCategoryId || null;
      }).filter(Boolean)
    )];
    setCatId(catIds.length === 1 ? catIds[0] : '');
  }, [selectedOfferingIds, pledges, hasPledges, receiptType]);

  // Gold and Item receipts don't use a donation category - clear it when type changes to those
  useEffect(() => {
    if (receiptType === 'gold' || receiptType === 'item') setCatId('');
  }, [receiptType]);

  // -- Sync handlers ---------------------------------------------------------

  const handleReceiptTypeChange = useCallback((type) => {
    setReceiptType(type);

    // Reset the whole screen to its first-open defaults, then apply the
    // type-specific defaults below. (Receipt date and financial year — the
    // top-of-form selectors — are intentionally left as the user set them.)
    setSelected([]); setPayAmounts({}); setPledges([]);
    setDonorId(''); setDonorName(''); setDonorPhone('');
    setCatId('');
    setAmountInr('');
    setGoldGrams(''); setGoldPurity('22'); setValuationRate(''); setGoldManualAmt('');
    setReferenceNo(''); setNarration('');
    setManualReceiptNo('');
    setAutoSettleAmount('');
    setShowAdvance(allowAdvancePaymentRef.current);
    setPledgeFilterCategory('');
    setPledgeFilterFromDate(''); setPledgeFilterToDate('');
    setSendEmail(true);

    const filtered = debitAccountsForType(type, allAccounts);
    if (type === 'gold' || type === 'item') {
      setPaymentMode(type === 'gold' ? 'gold' : 'item');
      const defAcc = filtered.find(a => a.isSystem) || filtered[0] || null;
      setDebitAcctId(defAcc?.id || '');
      const targetCatName = type === 'gold' ? 'Gold' : 'Item';
      const cat = categories.find(c => c.name === targetCatName);
      if (cat) setCatId(cat.id);
    } else if (type === 'bhandar') {
      setPaymentMode('cash');
      const best = defaultDebitForMode('cash', filtered);
      setDebitAcctId(best?.id || '');
      const bhandarCat = categories.find(c => c.name === 'Bhandar');
      if (bhandarCat) setCatId(bhandarCat.id); else setCatId('');
    } else {
      setCatId('');
      setPaymentMode('cash');
      const best = defaultDebitForMode('cash', filtered);
      setDebitAcctId(best?.id || '');
    }
  }, [allAccounts, categories]);

  const handlePaymentModeChange = useCallback((mode) => {
    setPaymentMode(mode);
    const filtered = debitAccountsForType(receiptType, allAccounts);
    const best = defaultDebitForMode(mode, filtered);
    setDebitAcctId(best?.id || '');
  }, [receiptType, allAccounts]);

  const handleDebitAcctChange = useCallback((id) => {
    setDebitAcctId(id);
    const acct = allAccounts.find(a => String(a.id) === String(id));
    if (!acct) return;
    const inferred = modeForAccount(acct, paymentMode);
    if (inferred !== paymentMode) setPaymentMode(inferred);
  }, [allAccounts, paymentMode]);

  // -- Pledge toggle ---------------------------------------------------------
  const togglePledge = (id) => {
    const pl = pledges.find((p) => p.id === id);
    if (selectedOfferingIds.includes(id)) {
      setSelected((prev) => prev.filter((x) => x !== id));
      setPayAmounts((prev) => { const n = { ...prev }; delete n[id]; return n; });
    } else {
      setSelected((prev) => [...prev, id]);
      setPayAmounts((prev) => ({ ...prev, [id]: String(pl?.pendingAmount || '') }));
    }
  };
  const toggleAll = () => {
    if (allVisibleSelected) {
      const visibleIds = new Set(visiblePledges.map((p) => p.id));
      setSelected((prev) => prev.filter((id) => !visibleIds.has(id)));
      setPayAmounts((prev) => {
        const next = { ...prev };
        visiblePledges.forEach((p) => delete next[p.id]);
        return next;
      });
    } else {
      const newIds = visiblePledges.map((p) => p.id);
      setSelected((prev) => [...new Set([...prev, ...newIds])]);
      setPayAmounts((prev) => ({
        ...prev,
        ...Object.fromEntries(visiblePledges.map((p) => [p.id, String(p.pendingAmount)])),
      }));
    }
  };

  // -- Submit ----------------------------------------------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!debitAcctId) return toast.error('Select a debit account (Asset)');
    if (!financialYearId) return toast.error('Select a financial year');
    if (!isGold && !isItem && !hasPledges && !donationCategoryId) return toast.error('Select a donation category (for fund tracking)');
    if (isGold && !goldGrams) return toast.error('Enter gold weight in grams');
    if (!isGold && !isItem && totalAmount <= 0) return toast.error('Amount must be greater than zero');

    if (hasPledges) {
      const invalid = selectedOfferingIds.some((id) => {
        const pl = pledges.find((p) => p.id === id);
        const amt = parseFloat(payAmounts[id] || 0);
        return !Number.isFinite(amt) || amt <= 0 || amt > parseFloat(pl?.pendingAmount || 0);
      });
      if (invalid) return toast.error('Payment amounts must be > 0 and = pending balance');
    }

    setSaving(true);
    try {
      await createReceipt({
        derasarId: activeDerasarId,
        financialYearId,
        receiptDate, receiptType, paymentMode,
        debitLedgerAccountId: debitAcctId,
        donationCategoryId: donationCategoryId || undefined,
        offeringSettlements: hasPledges
          ? selectedOfferingIds.map((id) => ({ offeringId: id, settledAmount: parseFloat(payAmounts[id] || 0) }))
          : undefined,
        userId: (isAnonymous || isBhandar) ? undefined : donorId || undefined,
        amountInr: totalAmount,
        goldGrams: isGold ? parseFloat(goldGrams) : undefined,
        goldPurity: isGold ? parseFloat(goldPurity) : undefined,
        valuationRate: isGold && valuationRate ? parseFloat(valuationRate) : undefined,
        referenceNo: ['cheque', 'upi', 'bank_transfer'].includes(paymentMode) ? referenceNo || undefined : undefined,
        narration: narration || (isAnonymous ? 'Anonymous Donation' : isBhandar ? 'Bhandar Collection' : undefined),
        manualReceiptNo: manualReceiptNo || undefined,
        donorName: isAnonymous ? (donorName?.trim() || 'Anonymous') : isBhandar ? 'Bhandar Collection' : undefined,
        sendEmail: sendEmail && !!donorId,
      });
      toast.success('Receipt created');
      navigate('/accounting/receipts');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create receipt');
    } finally {
      setSaving(false);
    }
  };

  if (loadingRef) return <div className="loading"><div className="spinner" /></div>;

  return (
    <div className={styles.formPage}>
      <div className="page-header">
        <h2><FiInbox /> New Receipt</h2>
        <button className="btn btn-ghost btn-sm" onClick={() => navigate('/accounting/receipts')}>
          <FiArrowLeft /> Back
        </button>
      </div>

      {unlockedFinancialYears.length === 0 && (
        <div className={`${aStyles.infoBox} ${aStyles.infoBoxWarn}`} style={{ marginBottom: 16 }}>
          <FiAlertTriangle /> No unlocked financial year is available. Create a year or unlock an existing year before creating receipts.
        </div>
      )}

      <div className={styles.formScrollArea}>
      <form id="new-receipt-form" onSubmit={handleSubmit}>
        <div className="card">

          {/* -- Section 1: Date, Manual Receipt No & Receipt Type -- */}
          <div className={styles.section}>
            <div className={styles.sectionLabel}>Receipt Details</div>
            <div className={styles.topRow}>
              <div className="form-group">
                <label>Receipt Date *</label>
                <input type="date" value={receiptDate} required
                  onChange={e => setReceiptDate(e.target.value)}
                  onBlur={e => {
                    const v = e.target.value;
                    if (!v || v.length < 10) return;
                    if (fyStart && v < fyStart) setReceiptDate(fyStart);
                    else if (fyEnd && v > fyEnd) setReceiptDate(fyEnd);
                  }} />
                {fyStart && fyEnd && (
                  <span className={styles.hint}>
                    Valid range: {fyStart.split('-').reverse().join('-')} to {fyEnd.split('-').reverse().join('-')}
                  </span>
                )}
              </div>
              <div className="form-group">
                <label>Manual Receipt No <em className={styles.optLabel}>(optional)</em></label>
                <input
                  value={manualReceiptNo}
                  placeholder="e.g. HW-2024-001"
                  onChange={(e) => setManualReceiptNo(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Receipt Type *</label>
                <div className={styles.modeRow}>
                  {RECEIPT_TYPES.map(t => (
                    <label key={t.value}
                      className={`${styles.modeChip} ${receiptType === t.value ? styles.modeChipOn : ''}`}>
                      <input type="radio" name="receiptType" value={t.value}
                        checked={receiptType === t.value}
                        onChange={() => handleReceiptTypeChange(t.value)} />
                      {t.label}
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {(isAnonymous || isBhandar) && (
              <div className={styles.row2}>
                {isAnonymous && (
                  <div className="form-group">
                    <label>Donor Name <em className={styles.optLabel}>(optional — defaults to "Shah A/C")</em></label>
                    <input
                      value={donorName}
                      placeholder="Enter donor name for anonymous entry"
                      onChange={(e) => setDonorName(e.target.value)}
                    />
                  </div>
                )}
                {isBhandar && (
                  <div className="form-group">
                    <label>Donor</label>
                    <div className={styles.autoField}>
                      Bhandar Collection (anonymous)
                      <span className={styles.hint}>Bhandar receipts are always anonymous</span>
                    </div>
                  </div>
                )}
                <div className="form-group">
                  <label>Amount (₹) *</label>
                  <input type="number" min="1" step="1" value={amountInr} required
                    placeholder="Enter amount" className={styles.noSpin}
                    onKeyDown={blockNonInteger} onWheel={blockWheel}
                    onChange={e => setAmountInr(toIntString(e.target.value))} />
                </div>
              </div>
            )}
          </div>

          <div className={styles.divider} />

          {/* -- Section 2: Donor & Pledges (skip for anonymous and bhandar) -- */}
          {!isAnonymous && !isBhandar && (
            <>
              <div className={styles.section}>
                <div className={styles.sectionLabel}>Donor & Offering Settlement</div>
                <div className={styles.row2}>
                  <div className="form-group">
                    <label>Member <em className={styles.optLabel}>(optional: leave blank for walk-in)</em></label>
                    <OfferingsMemberSelect
                      value={donorId}
                      displayValue={selectedDonor?.name || ''}
                      displayEmail={selectedDonor?.email || ''}
                      displayPhone={donorPhone}
                      options={personUsers_mapped}
                      onSelect={(m) => { setDonorId(m.id); setDonorPhone(m.phone || ''); setSelected([]); setPayAmounts({}); }}
                    />
                    {donorId && (
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        style={{ marginTop: 6, padding: '3px 10px', fontSize: '0.78rem' }}
                        onClick={() => { setDonorId(''); setDonorPhone(''); setSelected([]); setPayAmounts({}); }}
                      >× Clear (walk-in donation)
                      </button>
                    )}
                  </div>
                </div>

                {donorId && !isGold && !isItem && (
                  <div className={styles.pledgeTableWrap}>
                    {loadingPledges ? (
                      <div className={styles.pledgeEmpty}>
                        <div className="spinner" style={{ width: 18, height: 18 }} />&nbsp;Loading offerings…
                      </div>
                    ) : pledges.length === 0 ? (
                      <div className={styles.pledgeEmpty}>No pending offerings found for this member.</div>
                    ) : (
                      <>
                        <div className={styles.pledgeTableHint}>
                          Select offerings to settle — enter custom payment amount per row
                        </div>
                        <div className={styles.pledgeFilterRow}>
                          <div className="form-group" style={{ margin: 0 }}>
                            <label>Category</label>
                            <select value={pledgeFilterCategory} onChange={(e) => setPledgeFilterCategory(e.target.value)}>
                              <option value="">All categories</option>
                              {pledgeCategoryOptions.map((cat) => (
                                <option key={cat.id} value={cat.id}>{cat.name}</option>
                              ))}
                            </select>
                          </div>
                          <div className="form-group" style={{ margin: 0 }}>
                            <label>Date From</label>
                            <input type="date" value={pledgeFilterFromDate} onChange={(e) => setPledgeFilterFromDate(e.target.value)} />
                          </div>
                          <div className="form-group" style={{ margin: 0 }}>
                            <label>Date To</label>
                            <input type="date" value={pledgeFilterToDate} onChange={(e) => setPledgeFilterToDate(e.target.value)} />
                          </div>
                          <button type="button" className="btn btn-ghost btn-sm" onClick={clearPledgeFilters}>
                            Clear filters
                          </button>
                        </div>
                        <div className={styles.pledgeActionRow}>
                          <div className={styles.advanceBlock}>
                            <label className={styles.advanceToggle}>
                            <input
                              type="checkbox"
                              checked={showAdvancePayments}
                              onChange={(e) => setShowAdvance(e.target.checked)}
                            />
                              <span className={styles.toggleTrack} data-on={showAdvancePayments}>
                                <span className={styles.toggleThumb} />
                              </span>
                              <span>Allow advance payment</span>
                          </label>
                            <span className={styles.advanceHint}>
                            {showAdvancePayments
                              ? 'Showing all offerings including future offering dates'
                              : `Showing offerings with offering date on or before ${receiptDate}`}
                          </span>
                          </div>
                          <div className={styles.partialSettle}>
                            <div className={styles.partialAmountField}>
                            <label>Partial amount</label>
                            <input
                              type="number"
                              min="1"
                              step="1"
                              inputMode="numeric"
                              placeholder="e.g. 5000"
                              value={autoSettleAmount}
                              onKeyDown={blockNonInteger} onWheel={blockWheel}
                              onChange={(e) => setAutoSettleAmount(toIntString(e.target.value))}
                              className={styles.payAmountInput}
                            />
                          </div>
                          <button type="button" className="btn btn-secondary btn-sm" onClick={applyAutoSettleAmount}>
                            Apply to oldest
                          </button>
                            <span className={styles.advanceHint}>
                            Applies to visible offerings first, oldest to newest.
                          </span>
                          </div>
                        </div>
                        <div className="table-container">
                          <table>
                            <thead>
                              <tr>
                                <th style={{ width: 36 }}>
                                  <input type="checkbox"
                                    checked={allVisibleSelected}
                                    onChange={toggleAll} />
                                </th>
                                <th>Category</th>
                                <th>Offering Date</th>
                                <th>Offered Amt</th>
                                <th>Paid</th>
                                <th>Pending</th>
                                <th>Pay Amount</th>
                                <th>Status</th>
                                <th>Due</th>
                              </tr>
                            </thead>
                            <tbody>
                              {visiblePledges.map(pl => {
                                const sel = selectedOfferingIds.includes(pl.id);
                                return (
                                  <tr key={pl.id}
                                    className={`${styles.pledgeRow} ${sel ? styles.pledgeRowSel : ''}`}
                                    onClick={() => togglePledge(pl.id)}>
                                    <td onClick={e => e.stopPropagation()}>
                                      <input type="checkbox" checked={sel} onChange={() => togglePledge(pl.id)} />
                                    </td>
                                    <td>{pl.DonationCategory?.name || pl.donationCategory?.name || pl.donationCategoryId?.name || '—'}</td>
                                    <td>{formatDate(pl.offeringDate)}</td>
                                    <td>{fmtCurrency(pl.offeredAmount)}</td>
                                    <td>{fmtCurrency(pl.paidAmount)}</td>
                                    <td><strong>{fmtCurrency(pl.pendingAmount)}</strong></td>
                                    <td onClick={(e) => e.stopPropagation()}>
                                      {sel ? (
                                        <input
                                          type="number"
                                          min="1"
                                          step="1"
                                          inputMode="numeric"
                                          max={Math.floor(pl.pendingAmount)}
                                          value={payAmounts[pl.id] ?? ''}
                                          className={styles.payAmountInput}
                                          onKeyDown={blockNonInteger} onWheel={blockWheel}
                                          onChange={(e) => setPayAmounts((prev) => ({ ...prev, [pl.id]: toIntString(e.target.value) }))}
                                          onBlur={(e) => {
                                            const val = Math.round(parseFloat(e.target.value));
                                            const max = Math.floor(parseFloat(pl.pendingAmount));
                                            const clamped = !Number.isFinite(val) || val > max ? max : val;
                                            setPayAmounts((prev) => ({ ...prev, [pl.id]: String(clamped) }));
                                          }}
                                        />
                                      ) : <span style={{ color: 'var(--color-text-muted)' }}>—</span>}
                                    </td>
                                    <td>
                                      <span className={`badge ${pl.status === 'partial' ? 'badge-pending' : 'badge-info'}`}>
                                        {pl.status}
                                      </span>
                                    </td>
                                    <td>{formatDate(pl.dueDate)}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                            {hasPledges && (
                              <tfoot>
                                <tr>
                                  <td colSpan={6}>
                                    <strong>{selectedOfferingIds.length} offering{selectedOfferingIds.length > 1 ? 's' : ''} selected</strong>
                                  </td>
                                  <td><strong>{fmtCurrency(totalAmount)}</strong></td>
                                  <td colSpan={2} />
                                </tr>
                              </tfoot>
                            )}
                          </table>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
              <div className={styles.divider} />
            </>
          )}

          {/* -- Section 3: Accounting Entries -- */}
          <div className={styles.section}>
            <div className={styles.sectionLabel}>Accounting Entries</div>
            <div className={styles.row2}>
              {/* Debit account — Asset */}
              <div className="form-group">
                <label>Debit Account (Asset) *</label>
                <LedgerAccountSelect
                  value={debitAcctId}
                  onChange={(id) => handleDebitAcctChange(id)}
                  accounts={debitAccounts}
                  balanceMap={balanceMap}
                  placeholder="— Select asset account —"
                />
                <span className={styles.hint}>
                  {isGold ? 'Gold inventory account' : isItem ? 'Asset account for the item' : 'Cash or bank account receiving the money'}
                </span>
              </div>

              {/* Donation Category — hidden for Gold and Item */}
              {!isGold && !isItem && (
                <div className="form-group">
                  <label>Donation Category {!hasPledges && !isBhandar ? '*' : ''} <em className={styles.optLabel}>(for fund tracking)</em></label>
                  {isBhandar ? (
                    <div className={styles.autoField}>
                      {categories.find(c => c.id === donationCategoryId)?.name || 'Bhandar'}
                      <span className={styles.hint}>(Locked to Bhandar category)</span>
                    </div>
                  ) : hasPledges ? (
                    <div className={styles.autoField}>
                      {selectedOfferingIds.length === 1 && categories.find(c => c.id === donationCategoryId)
                        ? categories.find(c => c.id === donationCategoryId).name
                        : `${selectedOfferingIds.length} categories (from offerings)`}
                      <span className={styles.hint}>(Auto-derived from selected offerings)</span>
                    </div>
                  ) : (
                    <SearchableSelect
                      value={donationCategoryId}
                      onChange={(id) => setCatId(id)}
                      options={categories.map(c => ({ value: c.id, label: c.name }))}
                      placeholder="— Select category —"
                      searchPlaceholder="Search category…"
                    />
                  )}
                  <span className={styles.hint}>
                    Tracks which fund received this donation — not posted as a ledger entry
                  </span>
                </div>
              )}
            </div>

            {/* Journal entry preview */}
            {debitAcctId && totalAmount > 0 && (
              <div className={styles.journalPreview}>
                <div className={styles.journalRow}>
                  <span className={styles.journalDr}>Dr</span>
                  <span>{selectedDebitAcct?.name}</span>
                  <span className={styles.journalAmt}>{fmtCurrency(totalAmount)}</span>
                </div>
                <div className={styles.journalRow}>
                  <span className={styles.journalCr}>Cr</span>
                  <span>
                    {isGold ? 'Gold Income'
                      : isItem ? 'Item Income'
                      : isBhandar ? 'Bhandar Donation Income'
                      : 'Donation Income'}
                  </span>
                  <span className={styles.journalAmt}>{fmtCurrency(totalAmount)}</span>
                </div>
                {creditLineLabel && (
                  <div style={{ fontSize: '0.8em', color: 'var(--color-text-muted)', marginTop: 4 }}>
                    Fund tracked under: <strong>{creditLineLabel}</strong>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className={styles.divider} />

          {/* -- Section 4b: Narration only for bhandar (always cash, no mode selector) -- */}
          {isBhandar && (
            <>
              <div className={styles.section}>
                <div className={styles.sectionLabel}>Payment Details</div>
                <div className={styles.row2}>
                  <div className="form-group">
                    <label>Payment Mode</label>
                    <div className={styles.autoField}>
                      Cash (locked)
                      <span className={styles.hint}>Bhandar receipts are always cash</span>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Narration / Remarks</label>
                    <input value={narration} placeholder="Optional note"
                      onChange={e => setNarration(e.target.value)} />
                  </div>
                </div>
              </div>
              <div className={styles.divider} />
            </>
          )}

          {/* -- Section 4: Payment Mode + Narration (hidden for gold, item, and bhandar) -- */}
          {!isGold && !isItem && !isBhandar && (
            <>
              <div className={styles.section}>
                <div className={styles.sectionLabel}>Payment Details</div>
                <div className={styles.row2}>
                  <div className="form-group">
                    <label>Payment Mode *</label>
                    <div className={styles.modeRow}>
                      {PAYMENT_MODES.map(m => (
                        <label key={m.value}
                          className={`${styles.modeChip} ${paymentMode === m.value ? styles.modeChipOn : ''}`}>
                          <input type="radio" name="paymentMode" value={m.value}
                            checked={paymentMode === m.value}
                            onChange={() => handlePaymentModeChange(m.value)} />
                          {m.label}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    {['cheque', 'upi', 'bank_transfer'].includes(paymentMode) && (
                      <div className="form-group">
                        <label>
                          {paymentMode === 'cheque' ? 'Cheque Number' : paymentMode === 'upi' ? 'UPI Reference No' : 'Bank Reference No'}
                          <em style={{ fontWeight: 400, color: 'var(--color-text-muted)', fontSize: '0.85em' }}> (optional)</em>
                        </label>
                        <input value={referenceNo} placeholder={paymentMode === 'cheque' ? 'e.g. 001234' : 'e.g. TXN123456'}
                          onChange={e => setReferenceNo(e.target.value)} />
                      </div>
                    )}
                    <div className="form-group">
                      <label>Narration / Remarks</label>
                      <input value={narration} placeholder="Optional note"
                        onChange={e => setNarration(e.target.value)} />
                    </div>
                  </div>
                </div>
              </div>
              <div className={styles.divider} />
            </>
          )}

          {/* -- Section 5: Gold Details -- */}
          {isGold && (
            <>
              <div className={styles.section}>
                <div className={styles.sectionLabel}>Gold Details</div>
                <div className={styles.row4}>
                  <div className="form-group">
                    <label>Weight (grams) *</label>
                    <input type="number" min="0" step="0.001" value={goldGrams} required
                      placeholder="e.g. 10.5" className={styles.noSpin} onWheel={blockWheel}
                      onChange={e => setGoldGrams(e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label>Purity (Karat)</label>
                    <input type="number" min="1" max="24" value={goldPurity}
                      className={styles.noSpin} onWheel={blockWheel}
                      onChange={e => setGoldPurity(e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label>Rate per gram (₹)</label>
                    <input type="number" min="0" step="0.01" value={valuationRate}
                      placeholder="Optional" className={styles.noSpin} onWheel={blockWheel}
                      onChange={e => { setValuationRate(e.target.value); setGoldManualAmt(''); }} />
                    <span className={styles.hint}>Leave blank to enter value manually</span>
                  </div>
                  <div className="form-group">
                    <label>INR Value</label>
                    {computedGoldAmt !== null ? (
                      <input readOnly value={`₹ ${fmt(computedGoldAmt)}`} className={styles.readonlyInput} />
                    ) : (
                      <input type="number" min="1" step="1" value={goldManualAmt}
                        placeholder="Enter value (optional)" className={styles.noSpin}
                        onKeyDown={blockNonInteger} onWheel={blockWheel}
                        onChange={e => setGoldManualAmt(toIntString(e.target.value))} />
                    )}
                  </div>
                </div>
                <div className="form-group" style={{ maxWidth: 480 }}>
                  <label>Narration / Remarks</label>
                  <input value={narration} placeholder="Optional note"
                    onChange={e => setNarration(e.target.value)} />
                </div>
              </div>
              <div className={styles.divider} />
            </>
          )}

          {/* -- Section 5.5: Item Details -- */}
          {isItem && (
            <>
              <div className={styles.section}>
                <div className={styles.sectionLabel}>Item Details</div>
                <div className={styles.row2}>
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label>Item Description (e.g., Chair, Table, Laptop) *</label>
                    <input value={narration} required placeholder="Describe the item(s) donated"
                      onChange={e => setNarration(e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label>Estimated Value (₹) <em className={styles.optLabel}>(optional)</em></label>
                    <input type="number" min="1" step="1" value={amountInr}
                      placeholder="Enter value if known" className={styles.noSpin}
                      onKeyDown={blockNonInteger} onWheel={blockWheel}
                      onChange={e => setAmountInr(toIntString(e.target.value))} />
                    <span className={styles.hint}>Used for accounting valuation</span>
                  </div>
                </div>
              </div>
              <div className={styles.divider} />
            </>
          )}


        </div>
      </form>
      </div>

      <div className={styles.pageFooter}>
        <div className={styles.pageFooterLeft}>
          {hasPledges && (
            <span className={styles.pledgeCount}>
              {selectedOfferingIds.length} offering{selectedOfferingIds.length > 1 ? 's' : ''} selected
            </span>
          )}
          {isGold && goldGrams && (
            <span className={styles.pledgeCount}>
              {goldGrams}g gold · {goldPurity}k
            </span>
          )}
          {!isAnonymous && (
            <label className={`${styles.emailToggle} ${!donorId ? styles.emailToggleDisabled : ''}`}>
              <span className={styles.toggleTrack} data-on={sendEmail && !!donorId}>
                <span className={styles.toggleThumb} />
              </span>
              <input type="checkbox" checked={sendEmail && !!donorId}
                disabled={!donorId}
                onChange={e => setSendEmail(e.target.checked)} />
              <span className={styles.toggleLabel}>
                Send PDF receipt to donor's email
                {!donorId && <span className={styles.toggleHint}> (select a donor first)</span>}
              </span>
            </label>
          )}
        </div>
        <div className={styles.pageFooterRight}>
          <div className={styles.totalBox}>
            <span className={styles.totalLabel}>Receipt Total</span>
            <span className={styles.totalValue}>
              {isGold && computedGoldAmt === null && !goldManualAmt
                ? <span className={styles.totalMuted}>— not valued —</span>
                : fmtCurrency(totalAmount)}
              {hasPledges && <span className={styles.totalNote}> (auto)</span>}
              {isGold && computedGoldAmt !== null && <span className={styles.totalNote}> (computed)</span>}
            </span>
          </div>
          <div className={styles.actionBtns}>
            <button type="button" className="btn btn-ghost"
              onClick={() => navigate('/accounting/receipts')}>
              Cancel
            </button>
            <button type="submit" form="new-receipt-form" className="btn btn-primary" disabled={saving || !financialYearId}>
              {saving ? 'Saving…' : <><FiSave /> Create Receipt</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewReceiptsPage;
